
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Investor } from "@/types/investor";
import { useInvestorAI } from "@/hooks/useInvestorAI";

/**
 * Hook for handling single investor processing operations
 */
export const useSingleProcessingOperations = (
  investors: Investor[],
  setInvestors: (investors: Investor[]) => void,
  processedIds: Set<string>,
  setProcessedIds: (processedIds: Set<string>) => void,
  selectedInvestor: Investor | null,
  setSelectedInvestor: (investor: Investor | null) => void,
  contacts: any[] // Using any[] to avoid circular dependencies, but this is the Contact[] type
) => {
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  
  const {
    handleProcessInvestor: aiHandleProcessInvestor,
    handleEnrichInvestor: aiHandleEnrichInvestor,
    isEnriching,
    setIsEnriching
  } = useInvestorAI();

  /**
   * Process a single investor
   */
  const handleProcessInvestor = useCallback(async (investor: Investor, relevanceContext: string) => {
    // Prevent duplicate processing
    if (processingIds.has(investor.id)) {
      toast.info("This investor is already being processed");
      return investor;
    }

    try {
      // Mark investor as being processed
      setProcessingIds(prev => new Set(prev).add(investor.id));
      
      return aiHandleProcessInvestor(
        investor,
        relevanceContext,
        investors,
        processedIds,
        async (updatedInvestors) => {
          setInvestors(updatedInvestors);
          // Directly save to database immediately
          return Promise.resolve(true);
        },
        (newProcessedIds) => {
          setProcessedIds(newProcessedIds);
        }
      );
    } finally {
      // Always clear processing status
      setProcessingIds(prev => {
        const updated = new Set(prev);
        updated.delete(investor.id);
        return updated;
      });
    }
  }, [investors, processedIds, setInvestors, setProcessedIds, processingIds, aiHandleProcessInvestor]);

  /**
   * Enrich a single investor
   */
  const handleEnrichInvestor = useCallback(async (investor: Investor, relevanceContext: string) => {
    if (!relevanceContext) {
      toast.error("Please set relevance criteria before enriching investors");
      return investor;
    }
    
    // Prevent duplicate processing
    if (processingIds.has(investor.id)) {
      toast.info("This investor is already being processed");
      return investor;
    }
    
    try {
      // Mark investor as being processed
      setProcessingIds(prev => new Set(prev).add(investor.id));
      
      const enrichedInvestor = await aiHandleEnrichInvestor(
        investor,
        relevanceContext,
        investors,
        async (updatedInvestors) => {
          // Set the investors state immediately for UI updates
          setInvestors(updatedInvestors);
          
          // Save directly to database - return true to indicate success
          return Promise.resolve(true);
        },
        (updatedInvestor) => {
          // Update selected investor if it's the currently selected one
          if (selectedInvestor && selectedInvestor.id === updatedInvestor.id) {
            setSelectedInvestor(updatedInvestor);
          }
        }
      );
      
      return enrichedInvestor;
    } catch (error) {
      console.error("Failed to enrich investor:", error);
      toast.error("Failed to enrich investor data");
      throw error;
    } finally {
      // Always clear processing status
      setProcessingIds(prev => {
        const updated = new Set(prev);
        updated.delete(investor.id);
        return updated;
      });
    }
  }, [investors, setInvestors, processingIds, selectedInvestor, setSelectedInvestor, aiHandleEnrichInvestor]);

  /**
   * Update a single investor
   */
  const handleInvestorUpdate = useCallback((updatedInvestor: Investor) => {
    const updatedInvestors = investors.map(inv => 
      inv.id === updatedInvestor.id ? updatedInvestor : inv
    );
    setInvestors(updatedInvestors);
    
    // Update selected investor if it's the one being updated
    if (selectedInvestor && selectedInvestor.id === updatedInvestor.id) {
      setSelectedInvestor(updatedInvestor);
    }
    
    // The saving operation will be handled by the parent component
  }, [investors, setInvestors, selectedInvestor, setSelectedInvestor]);

  return {
    processingIds,
    isEnriching,
    setIsEnriching,
    handleProcessInvestor,
    handleEnrichInvestor,
    handleInvestorUpdate
  };
};
